import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchCreatives, fetchCreativeCount, toggleFavorite, deleteCreative, markUsed,
} from "../../services/creativeLibraryService";
import CreativeImport from "./CreativeImport";

// Bibliothèque de créatives — galerie dense et paginée (échelle 1000+),
// façon Higgsfield. Chaque visuel est éditable (retouche dans Cinema Studio)
// et réutilisable dans une pub (AI Launcher) ou un produit Shopify.
const TYPES = [["", "Tout"], ["image", "Images"], ["video", "Vidéos"], ["audio", "Voix"]];
const PAGE_SIZE = 24;

// Vignette vidéo : aperçu muet au survol (preload="metadata" pour ne pas
// télécharger toutes les vidéos de la page), clic = lecture plein écran.
const VideoThumb = ({ src, onOpen }) => {
  const ref = useRef(null);
  return (
    <div
      className="w-full h-full relative cursor-pointer bg-[#0F0B2D]"
      onMouseEnter={() => ref.current?.play().catch(() => {})}
      onMouseLeave={() => { if (ref.current) { ref.current.pause(); ref.current.currentTime = 0; } }}
      onClick={onOpen}
    >
      <video
        ref={ref}
        src={src}
        muted
        loop
        playsInline
        preload="metadata"
        className="w-full h-full object-cover"
      />
      {/* Pastille lecture — disparaît au survol pour laisser voir l'aperçu */}
      <span className="absolute inset-0 flex items-center justify-center group-hover:opacity-0 transition pointer-events-none">
        <span className="w-9 h-9 rounded-full bg-black/55 text-white text-sm flex items-center justify-center">▶</span>
      </span>
    </div>
  );
};

const Tile = ({ c, onFav, onDelete, onEdit, onUse, onPlay }) => (
  <div className="group relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-square flex flex-col">
    <div className="flex-1 relative">
      {c.type === "video" ? (
        <VideoThumb src={c.url} onOpen={() => onPlay(c)} />
      ) : c.type === "audio" ? (
        <div className="w-full h-full flex flex-col items-center justify-center text-[#221C51] bg-indigo-50">
          <span className="text-3xl">🎙️</span><span className="text-[10px] mt-1">Voix off</span>
        </div>
      ) : (
        <img src={c.url} alt={c.prompt || "créative"} loading="lazy" className="w-full h-full object-cover" />
      )}

      {/* Favori — masqué sur les créas de la bibliothèque publique (pas les siennes) */}
      {!c.is_public && (
        <button onClick={() => onFav(c)} title="Favori"
          className={`absolute top-1.5 left-1.5 w-6 h-6 rounded-full text-xs flex items-center justify-center transition ${
            c.favorite ? "bg-amber-400 text-white" : "bg-black/30 text-white opacity-0 group-hover:opacity-100"}`}>
          ★
        </button>
      )}
      {c.is_public && (
        <span className="absolute top-1.5 left-1.5 text-[9px] font-bold bg-fuchsia-600 text-white px-1.5 py-0.5 rounded-full">
          Ads Library
        </span>
      )}

      {/* Modèle utilisé — toujours visible, comme le "quel modèle a généré ça" de Higgsfield */}
      {c.model_id && (
        <span className="absolute top-1.5 right-1.5 text-[9px] font-semibold bg-black/50 text-white px-1.5 py-0.5 rounded-full max-w-[80%] truncate">
          {c.model_id}
        </span>
      )}

      {/* Actions au survol */}
      <div className="absolute inset-x-0 bottom-0 p-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition bg-gradient-to-t from-black/60 to-transparent">
        {c.type === "image" && !c.is_public && (
          <button onClick={() => onEdit(c)} className="flex-1 bg-white/95 text-fuchsia-700 text-[11px] font-semibold rounded-md py-1 hover:bg-white">✎ Modifier</button>
        )}
        {c.type !== "audio" && (
          <button onClick={() => onUse(c)} className="flex-1 bg-[#221C51] text-white text-[11px] font-semibold rounded-md py-1 hover:bg-[#2e265e]">Utiliser</button>
        )}
        <a href={c.url} target="_blank" rel="noreferrer" title="Ouvrir" className="w-7 flex items-center justify-center bg-white/95 text-[#221C51] text-[11px] rounded-md py-1 hover:bg-white">↗</a>
        {/* Suppression réservée au propriétaire — jamais sur la bibliothèque publique */}
        {!c.is_public && (
          <button onClick={() => onDelete(c)} title="Supprimer" className="w-7 bg-white/95 text-red-600 text-[11px] rounded-md py-1 hover:bg-white">🗑</button>
        )}
      </div>
    </div>
    {c.type === "audio" && <audio controls src={c.url} className="w-full h-7 shrink-0" />}
  </div>
);

const CreativeLibrary = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("mine"); // "mine" | "library" (Ads Library OneBotAds)
  const [type, setType] = useState("");
  const [fav, setFav] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [useTarget, setUseTarget] = useState(null); // créative en cours de réutilisation
  const [counts, setCounts] = useState(null); // { total, byType: { image, video, audio } }
  const [playing, setPlaying] = useState(null); // vidéo ouverte en plein écran

  const load = useCallback(() => {
    fetchCreatives({ scope, type, favorite: fav ? "true" : undefined, q: q || undefined, page, pageSize: PAGE_SIZE })
      .then(setData)
      .catch(() => toast.error("Chargement de la bibliothèque impossible."));
  }, [scope, type, fav, q, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { fetchCreativeCount(scope).then(setCounts).catch(() => {}); }, [data, scope]);

  const onFav = async (c) => {
    try { await toggleFavorite(c.id, !c.favorite); load(); } catch { toast.error("Action impossible."); }
  };
  const onDelete = async (c) => {
    if (!window.confirm("Supprimer cette créative ?")) return;
    try { await deleteCreative(c.id); load(); } catch { toast.error("Suppression impossible."); }
  };
  // Retouche : ouvre Cinema Studio avec l'image source pré-chargée.
  const onEdit = (c) => navigate(`/dashboard/cinema-studio?refine=${encodeURIComponent(c.url)}`);

  // Réutilisation : AI Launcher (pub) ou Shopify Ads, avec l'image passée en param.
  const reuseIn = async (c, channel) => {
    try { await markUsed(c.id, channel, ""); } catch { /* traçabilité best-effort */ }
    setUseTarget(null);
    if (channel === "shopify") navigate(`/dashboard/shopify-ads?creative=${encodeURIComponent(c.url)}`);
    else navigate(`/dashboard/create-ad-ai?creative=${encodeURIComponent(c.url)}`);
  };

  const totalPages = data?.totalPages || 1;

  return (
    <div className="p-2">
      {/* Ad Launcher — import de créatives (drag & drop, Google Drive) — seulement sur "Mes créas" */}
      {scope === "mine" && <CreativeImport onImported={load} />}

      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#221C51]">
            {scope === "library" ? "Ads Library" : "Bibliothèque de créatives"}
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">
            {scope === "library"
              ? `${data?.total ?? "…"} créative${(data?.total ?? 0) > 1 ? "s" : ""} OneBotAds — prêtes à réutiliser dans vos pubs et produits Shopify.`
              : `${data?.total ?? "…"} créative${(data?.total ?? 0) > 1 ? "s" : ""} — éditables et réutilisables dans vos pubs et produits Shopify.`}
          </p>
        </div>
        <button onClick={() => navigate("/dashboard/cinema-studio")}
          className="px-4 py-2 bg-fuchsia-600 text-white text-sm font-semibold rounded-xl hover:bg-fuchsia-700">
          + Générer des créatives
        </button>
      </div>

      {/* Mes créas / Ads Library */}
      <div className="flex items-center gap-2 mb-3">
        {[["mine", "Mes créas"], ["library", "Ads Library"]].map(([k, l]) => (
          <button key={k} onClick={() => { setScope(k); setType(""); setFav(false); setPage(1); }}
            className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold border ${scope === k ? "bg-fuchsia-600 text-white border-fuchsia-600" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {TYPES.map(([k, l]) => {
          const n = k ? counts?.byType?.[k] : counts?.total;
          return (
            <button key={k} onClick={() => { setType(k); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${type === k ? "bg-[#221C51] text-white border-[#221C51]" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
              {l}{n != null && <span className="ml-1.5 opacity-60">{n}</span>}
            </button>
          );
        })}
        {scope === "mine" && (
          <button onClick={() => { setFav((f) => !f); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${fav ? "bg-amber-400 text-white border-amber-400" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>★ Favoris</button>
        )}
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Rechercher un prompt…"
          className="flex-1 min-w-[160px] px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#221C51]" />
      </div>

      {/* Galerie dense */}
      {!data ? (
        <div className="flex justify-center py-20"><div className="w-7 h-7 border-4 border-[#221C51]/20 border-t-[#221C51] rounded-full animate-spin" /></div>
      ) : data.creatives.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <p className="text-sm">Aucune créative pour l'instant.</p>
          <button onClick={() => navigate("/dashboard/cinema-studio")} className="mt-3 text-fuchsia-600 font-semibold text-sm hover:underline">Créer ma première créative →</button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2.5">
            {data.creatives.map((c) => (
              <div key={c.id} className="relative">
                <Tile c={c} onFav={onFav} onDelete={onDelete} onEdit={onEdit} onUse={() => setUseTarget(c)} onPlay={setPlaying} />
                {/* Menu "Utiliser" */}
                {useTarget?.id === c.id && (
                  <div className="absolute z-10 right-0 bottom-9 bg-white rounded-xl shadow-lg border border-gray-100 py-1 w-40 text-sm">
                    <button onClick={() => reuseIn(c, "ads")} className="block w-full text-left px-3 py-2 hover:bg-gray-50">🚀 Dans une pub</button>
                    <button onClick={() => reuseIn(c, "shopify")} className="block w-full text-left px-3 py-2 hover:bg-gray-50">🛍️ Produit Shopify</button>
                    <button onClick={() => setUseTarget(null)} className="block w-full text-left px-3 py-2 text-gray-400 hover:bg-gray-50">Annuler</button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-6 flex-wrap">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40">←</button>
              <span className="text-sm text-gray-500 px-3">Page {page} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40">→</button>
            </div>
          )}
        </>
      )}

      {/* Lecteur vidéo plein écran — contrôles complets, son activable */}
      {playing && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setPlaying(null)}
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <video
              src={playing.url}
              controls
              autoPlay
              loop
              playsInline
              className="w-full max-h-[80vh] rounded-xl bg-black"
            />
            {/* Prompt de génération — réutilisable pour décliner d'autres créas */}
            {playing.prompt && (
              <div className="mt-3 bg-white/10 rounded-lg p-3">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-white/50 text-[10px] font-bold uppercase tracking-wider">Prompt utilisé</span>
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(playing.prompt);
                      toast.success("Prompt copié ✅");
                    }}
                    className="text-white/70 text-[10px] font-semibold hover:text-white"
                  >
                    ⧉ Copier
                  </button>
                </div>
                <p className="text-white/80 text-xs leading-relaxed">{playing.prompt}</p>
              </div>
            )}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <button
                onClick={() => { const c = playing; setPlaying(null); setUseTarget(c); }}
                className="px-3 py-1.5 bg-[#221C51] text-white text-xs font-semibold rounded-lg hover:bg-[#2e265e]"
              >
                Utiliser cette vidéo
              </button>
              {playing.prompt && (
                <button
                  onClick={() => navigate(`/dashboard/cinema-studio?prompt=${encodeURIComponent(playing.prompt)}`)}
                  className="px-3 py-1.5 bg-fuchsia-600 text-white text-xs font-semibold rounded-lg hover:bg-fuchsia-700"
                >
                  ✨ Réutiliser le prompt
                </button>
              )}
              <a href={playing.url} target="_blank" rel="noreferrer"
                className="px-3 py-1.5 bg-white/15 text-white text-xs font-semibold rounded-lg hover:bg-white/25">
                Ouvrir ↗
              </a>
            </div>
            <button
              onClick={() => setPlaying(null)}
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white text-[#221C51] font-bold shadow-lg"
              title="Fermer"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreativeLibrary;
